import { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../../main/theme/AppThemeContext';
import backgroundImg from '../../../main/assets/images/back1.jpg';
import '../../../main/theme/theme.css';
import './AuthSplitLayout.css';

type AuthSplitLayoutProps = {
  mode: 'login' | 'register';
  children: ReactNode;
  footer?: ReactNode;
};

const features = [
  { icon: '⚡', titleKey: 'login.authFeatureFastTitle', detailKey: 'login.authFeatureFastDetail' },
  { icon: '🛡️', titleKey: 'login.authFeatureSecureTitle', detailKey: 'login.authFeatureSecureDetail' },
  { icon: '📊', titleKey: 'login.authFeatureControlTitle', detailKey: 'login.authFeatureControlDetail' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 100, damping: 20 },
  },
};

const AuthSplitLayout = ({ mode, children, footer }: AuthSplitLayoutProps) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const isRegister = mode === 'register';

  return (
    <div className={`authSplitPage bcShell bcTheme-${theme} authSplitPage--${mode}`}>
      <div className="authSplitShell">
        {/* Form Pane */}
        <motion.div
          className="authSplitPane authSplitPane--form"
          initial={{ opacity: 0, x: isRegister ? -40 : 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ type: 'spring', stiffness: 80, damping: 18, delay: 0.1 }}
        >
          <motion.div
            className="authSplitCard"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {children}
          </motion.div>
          {footer && (
            <motion.p
              className="authSplitFooter"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {footer}
            </motion.p>
          )}
        </motion.div>

        {/* Media Pane */}
        <motion.div
          className="authSplitPane authSplitPane--media"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <div className="authSplitImage" style={{ backgroundImage: `url(${backgroundImg})` }} />
          <div className="authSplitOverlay" />

          {/* Brand Glass */}
          <motion.div
            className="authSplitGlass authSplitGlass--brand"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 80, damping: 16 }}
          >
            <div className="authSplitBrandIcon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <div>
              <div className="authSplitBrandTitle">TechPOS</div>
              <div className="authSplitBrandSubtitle">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                  <polyline points="10 17 15 12 10 7" />
                  <line x1="15" y1="12" x2="3" y2="12" />
                </svg>
                {t(isRegister ? 'login.authRegisterBrandSubtitle' : 'login.authLoginBrandSubtitle')}
              </div>
            </div>
          </motion.div>

          {/* Hero Copy */}
          <motion.div
            className="authSplitHeroCopy"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6, ease: 'easeOut' }}
          >
            <h1 className="authSplitHeroTitle">
              {t(isRegister ? 'login.authRegisterHeroTitle' : 'login.authLoginHeroTitle')}
            </h1>
            <p className="authSplitHeroText">
              {t('login.authHeroText')}
            </p>
          </motion.div>

          {/* Feature Stack */}
          <motion.div
            className="authSplitFeatureStack"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.12, delayChildren: 0.5 },
              },
            }}
          >
            {features.map((feature) => (
              <motion.div
                className="authSplitFeature"
                key={feature.titleKey}
                variants={{
                  hidden: { opacity: 0, y: 20, scale: 0.95 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: { type: 'spring', stiffness: 100, damping: 18 },
                  },
                }}
                whileHover={{ scale: 1.03, y: -2 }}
              >
                <div className="authSplitFeatureIcon">{feature.icon}</div>
                <div>
                  <div className="authSplitFeatureTitle">{t(feature.titleKey)}</div>
                  <div className="authSplitFeatureText">{t(feature.detailKey)}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Media Footer */}
          <motion.div
            className="authSplitMediaFooter"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <a href="#">{t('login.privacyPolicy')}</a>
            <span>/</span>
            <a href="#">{t('login.termsOfService')}</a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthSplitLayout;
